import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { CheckCircleOutline, WarningAmber } from '@mui/icons-material';
import { httpClient } from '../services/http-client';
import { useColorMode } from '../theme';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';

interface Point {
  period: string; label: string;
  assets: string; investments: string; debts: string; net_worth: string;
  /** Large Transfer: Clearing movement that month (issue #34). */
  in_transit?: string; warning?: string;
}

interface Reconciliation {
  account_id: string; name: string; account_type: string;
  opening: string; inflows: string; outflows: string;
  closing: string; discrepancy: string; entry_count: number;
}

interface OwnerSplit {
  owner_label: string; assets: string; investments: string;
  debts: string; net_worth: string;
}

interface LedgerNetWorth {
  from: string; to: string;
  current: Point;
  series: Point[];
  reconciliation: {
    period: string;
    accounts: Reconciliation[];
    unreconciled_count: number;
    largest_discrepancy: string;
    balanced: boolean;
  };
  by_owner: OwnerSplit[];
}

// A cross-account total, so it is reported in the household base currency.
// Components on this page call useBaseCurrency() so a change re-renders them.
const money = (n: string | number, digits = 0) => baseMoney(n, digits);

/**
 * Net worth, derived from the double-entry ledger.
 *
 * The reconciliation table is the point of this page: it shows
 * opening + inflows - outflows = closing for every account, so the headline
 * number is demonstrated rather than asserted.
 */
export const NetWorthPage: React.FC = () => {
  // Subscribes this page to the base currency so money() relabels on change.
  useBaseCurrency();
  const [data, setData] = useState<LedgerNetWorth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mode } = useColorMode();

  useEffect(() => {
    httpClient.get<LedgerNetWorth>('/net-worth/ledger')
      .then(setData)
      .catch((e) => setError(e?.message ?? 'Failed to load net worth'));
  }, []);

  // Chart geometry. Plotted as an area against a zero baseline so a negative
  // net worth would read as below the line rather than being clipped away.
  const chart = useMemo(() => {
    if (!data?.series?.length) return null;
    const w = 900, h = 220, pad = 8;
    const vals = data.series.map((p) => Number(p.net_worth));
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals);
    const span = max - min || 1;
    const x = (i: number) => (i / Math.max(1, data.series.length - 1)) * (w - pad * 2) + pad;
    const y = (v: number) => h - pad - ((v - min) / span) * (h - pad * 2);
    const line = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const area = `${line} L${x(vals.length - 1).toFixed(1)},${y(min).toFixed(1)} L${x(0).toFixed(1)},${y(min).toFixed(1)} Z`;
    return { w, h, line, area, zeroY: y(0), min, max };
  }, [data]);

  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;
  if (!data || !data.current) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Computing from the ledger…</Typography>
      </Box>
    );
  }

  const c = data.current;
  const r = data.reconciliation;
  const accent = mode === 'dark' ? '#2dd4a7' : '#0f766e';

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Position</Typography>
          <Typography variant="h3">Net worth</Typography>
          <Typography variant="body2" color="text.secondary">
            Derived from {data.series.length} months of ledger entries — not snapshots
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="overline" color="text.secondary">{c.label}</Typography>
          <Typography
            className="fw-num" variant="h3"
            sx={{ color: Number(c.net_worth) >= 0 ? 'var(--inflow-text)' : 'var(--outflow-text)' }}
          >
            {money(c.net_worth)}
          </Typography>
        </Box>
      </Stack>

      <Alert
        severity={r.balanced ? 'success' : 'warning'}
        icon={r.balanced ? <CheckCircleOutline /> : <WarningAmber />}
        sx={{ mb: 2 }}
      >
        {r.balanced
          ? `Every account reconciles: opening + inflows − outflows = closing across ${r.accounts.length} accounts, with zero discrepancy.`
          : `${r.unreconciled_count} account(s) do not reconcile. Largest discrepancy ${money(r.largest_discrepancy, 2)}.`}
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            <Metric label="Assets" value={money(c.assets)} varName="--inflow-text" />
            <Metric label="Investments" value={money(c.investments)} varName="--invest-text" />
            <Metric label="Debts" value={money(c.debts)} varName="--outflow-text" />
          </Stack>

          {chart && (
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <svg viewBox={`0 0 ${chart.w} ${chart.h}`} width="100%" height={220} preserveAspectRatio="none">
                <defs>
                  <linearGradient id="nwFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity="0.28" />
                    <stop offset="100%" stopColor={accent} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <line
                  x1="0" x2={chart.w} y1={chart.zeroY} y2={chart.zeroY}
                  stroke="var(--border)" strokeWidth="1" strokeDasharray="4 4"
                />
                <path d={chart.area} fill="url(#nwFill)" />
                <path d={chart.line} fill="none" stroke={accent} strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">{data.series[0]?.label}</Typography>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
              </Stack>
              {data.series.filter((p) => p.warning).map((p) => (
                <Typography key={p.period} variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}
                  data-testid="nw-in-transit">
                  {p.label}: {p.warning}
                </Typography>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {data.by_owner.length > 1 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">By owner</Typography>
            <Stack direction="row" spacing={4} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {data.by_owner.map((o) => (
                <Metric key={o.owner_label} label={o.owner_label} value={money(o.net_worth)} varName="--text" />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Reconciliation — {r.period?.slice(0, 7)}
            </Typography>
            <Tooltip title="Proves each closing balance from its opening balance and the month's movement.">
              <Chip size="small" label="opening + in − out = closing" />
            </Tooltip>
          </Stack>

          <TableContainer sx={{ maxHeight: '55vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Opening</TableCell>
                  <TableCell align="right">Inflows</TableCell>
                  <TableCell align="right">Outflows</TableCell>
                  <TableCell align="right">Closing</TableCell>
                  <TableCell align="right">Check</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {r.accounts.map((a) => {
                  const ok = Number(a.discrepancy) === 0;
                  return (
                    <TableRow key={a.account_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{a.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {a.entry_count} entries
                        </Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={a.account_type} /></TableCell>
                      <TableCell align="right" className="fw-num">{money(a.opening, 2)}</TableCell>
                      <TableCell align="right" className="fw-num" sx={{ color: 'var(--inflow-text)' }}>
                        {Number(a.inflows) ? `+${money(a.inflows, 2)}` : '—'}
                      </TableCell>
                      <TableCell align="right" className="fw-num" sx={{ color: 'var(--outflow-text)' }}>
                        {Number(a.outflows) ? `−${money(a.outflows, 2)}` : '—'}
                      </TableCell>
                      <TableCell align="right" className="fw-num" sx={{ fontWeight: 650 }}>
                        {money(a.closing, 2)}
                      </TableCell>
                      <TableCell align="right">
                        {ok ? (
                          <CheckCircleOutline fontSize="small" sx={{ color: 'var(--inflow-text)' }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: 'var(--danger-text)' }}>
                            {money(a.discrepancy, 2)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

const Metric: React.FC<{ label: string; value: string; varName: string }> = ({ label, value, varName }) => (
  <Box>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography className="fw-num" variant="h5" sx={{ color: `var(${varName})` }}>{value}</Typography>
  </Box>
);
